import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserManager, User as OidcUser } from 'oidc-client-ts';
import type { DemoUser } from '@/lib/demoUsers';
import {
  ExtendedUser,
  OidcUserProfile,
  oidcAuthority,
  oidcClientId,
  oidcConfig,
} from '@/lib/oidcConfig';
import { authStore } from '@/lib/authStore';
import {
  clearOidcLoginAttempt,
  clearOidcAutoLoginBlock,
  hasRecentOidcLoginAttempt,
  markOidcLoginAttempt,
  setOidcAutoLoginBlock,
} from '@/lib/authNavigationGuards';
import { canonicalizeRole, canonicalizeRoles } from '@/lib/roles';
import { useSessionStore } from '@/stores/session';
import { normalizeTenantUuidMap, resolveTenantUuidFromMap } from '@/lib/tenantScope';
import { alignLoopbackBaseToCurrentHost } from '@/lib/apiBase';
type AuthMode = 'demo' | 'oidc' | 'hybrid';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  authResolved: boolean;
  user: ExtendedUser | null;
  isDemoMode: boolean;
  currentTenant: string | null;
  currentTenantUuid: string | null;
  tenantRoles: string[];
  login: (returnPath?: string, options?: { loginHint?: string }) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => string | null;
  handleRedirectCallback: () => Promise<string>;
  switchTenant: (tenantId: string) => void;
  performDemoLogin?: (
    username: string,
    password: string,
    returnPath?: string,
  ) => Promise<'demo' | 'oidc' | false>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const allowBearerFallback =
  ((
    (import.meta.env.VITE_ALLOW_BEARER_FALLBACK ?? (import.meta.env.DEV ? '1' : '0')) as string
  ).trim() || (import.meta.env.DEV ? '1' : '0')) !== '0';

const resolveAuthMode = (): AuthMode => {
  const raw = (((import.meta.env.VITE_AUTH_MODE ?? '') as string).trim() || '').toLowerCase();
  if (raw === 'demo' || raw === 'oidc' || raw === 'hybrid') {
    return raw;
  }
  return 'oidc';
};

type TenantRoleMap = Record<string, string[]>;

const ensureRoleInMap = (map: TenantRoleMap, tenant: string, role: string) => {
  const canonical = canonicalizeRole(role);
  if (!canonical) return;
  const tenantKey = (tenant ?? '').trim();
  if (!tenantKey) return;
  const current = map[tenantKey] ?? [];
  if (!current.includes(canonical)) {
    map[tenantKey] = [...current, canonical];
  }
};

const cloneTenantRoleMap = (source: TenantRoleMap): TenantRoleMap =>
  Object.entries(source).reduce<TenantRoleMap>((acc, [tenant, roles]) => {
    acc[tenant] = [...roles];
    return acc;
  }, {});

const mergeIntoTenantRoleMap = (target: TenantRoleMap, source?: TenantRoleMap) => {
  if (!source) return;
  Object.entries(source).forEach(([tenant, roles]) => {
    roles.forEach((role) => ensureRoleInMap(target, tenant, role));
  });
};

const LOGOUT_GUARD_KEY = 'rugira_logout_guard';
const LOGOUT_GUARD_TTL_MS = 30000; // 30 seconds - guard expires after this time

const markLoggedOut = () => {
  try {
    // Use localStorage with timestamp for persistence across page loads
    // The guard expires after LOGOUT_GUARD_TTL_MS to prevent permanent lockout
    localStorage.setItem(LOGOUT_GUARD_KEY, String(Date.now()));
    // Also set in sessionStorage as backup
    sessionStorage.setItem(LOGOUT_GUARD_KEY, '1');
    if (import.meta.env.DEV) {
      console.log('[auth] Logout guard set in localStorage and sessionStorage');
    }
  } catch {
    /* ignore */
  }
};

const isLogoutGuardActive = (): boolean => {
  try {
    // Check localStorage first (more persistent)
    const timestamp = localStorage.getItem(LOGOUT_GUARD_KEY);
    if (timestamp) {
      const guardAge = Date.now() - parseInt(timestamp, 10);
      if (guardAge < LOGOUT_GUARD_TTL_MS) {
        if (import.meta.env.DEV) {
          console.log('[auth] Logout guard active, age:', guardAge, 'ms');
        }
        return true;
      }
      // Guard expired, clear it
      localStorage.removeItem(LOGOUT_GUARD_KEY);
    }
    // Fallback to sessionStorage
    if (sessionStorage.getItem(LOGOUT_GUARD_KEY) === '1') {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
};

const clearLogoutGuard = () => {
  try {
    localStorage.removeItem(LOGOUT_GUARD_KEY);
    sessionStorage.removeItem(LOGOUT_GUARD_KEY);
  } catch {
    /* ignore */
  }
};

const persistAuth = (
  token: string | undefined,
  refreshToken: string | undefined,
  user: ExtendedUser,
  ttlMs?: number,
) => {
  clearOidcLoginAttempt();
  clearLogoutGuard();
  clearOidcAutoLoginBlock();
  authStore.setAuth(token, refreshToken, user, ttlMs);
};

const persistSessionUser = (user: ExtendedUser, ttlMs?: number) => {
  clearOidcLoginAttempt();
  clearLogoutGuard();
  clearOidcAutoLoginBlock();
  authStore.setSessionUser(user, ttlMs);
};

const mergeTenantRoleMaps = (
  primary?: TenantRoleMap,
  secondary?: TenantRoleMap,
): TenantRoleMap | undefined => {
  if (!primary && !secondary) return undefined;
  const result: TenantRoleMap = primary ? cloneTenantRoleMap(primary) : {};
  mergeIntoTenantRoleMap(result, secondary);
  return Object.keys(result).length ? result : undefined;
};

const normalizeTenantRoleClaim = (input: unknown): TenantRoleMap | undefined => {
  if (input == null) return undefined;

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      return normalizeTenantRoleClaim(parsed);
    } catch {
      const idx = trimmed.indexOf(':');
      if (idx === -1) return undefined;
      const map: TenantRoleMap = {};
      ensureRoleInMap(map, trimmed.slice(0, idx), trimmed.slice(idx + 1));
      return map;
    }
  }

  if (Array.isArray(input)) {
    let aggregate: TenantRoleMap | undefined;
    input.forEach((entry) => {
      const partial = normalizeTenantRoleClaim(entry);
      aggregate = mergeTenantRoleMaps(aggregate, partial);
    });
    return aggregate;
  }

  if (typeof input === 'object') {
    const map: TenantRoleMap = {};
    Object.entries(input as Record<string, unknown>).forEach(([tenant, value]) => {
      if (Array.isArray(value)) {
        value.forEach((role) => {
          if (typeof role === 'string') ensureRoleInMap(map, tenant, role);
        });
      } else if (typeof value === 'string') {
        const parsed = normalizeTenantRoleClaim(value);
        if (parsed) {
          mergeIntoTenantRoleMap(map, parsed);
        } else {
          ensureRoleInMap(map, tenant, value);
        }
      }
    });
    return Object.keys(map).length ? map : undefined;
  }

  return undefined;
};

const base64UrlDecode = (segment: string): string | null => {
  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padding = (4 - (normalized.length % 4 || 4)) % 4;
    const padded = normalized + '='.repeat(padding);
    if (typeof globalThis.atob === 'function') {
      return globalThis.atob(padded);
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(padded, 'base64').toString('utf-8');
    }
    return null;
  } catch {
    return null;
  }
};

const decodeJwtClaims = (token: string): Record<string, any> | null => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  const decoded = base64UrlDecode(parts[1]);
  if (!decoded) {
    return null;
  }
  try {
    return JSON.parse(decoded) as Record<string, any>;
  } catch {
    return null;
  }
};

const hasTokenSubject = (token?: string | null): boolean => {
  if (!token) return false;
  const claims = decodeJwtClaims(token);
  return typeof claims?.sub === 'string' && claims.sub.trim().length > 0;
};

const resolveApiBearerToken = (oidcUser: OidcUser): string => {
  const accessToken = oidcUser.access_token || '';
  if (hasTokenSubject(accessToken)) {
    return accessToken;
  }

  const idToken = oidcUser.id_token || '';
  if (hasTokenSubject(idToken)) {
    if (import.meta.env.DEV) {
      console.warn('[auth] OIDC access token is missing sub claim; using id_token for API bearer.');
    }
    return idToken;
  }

  return accessToken || idToken || '';
};

const deriveJwtTtlFromClaims = (claims: any): number | undefined => {
  const exp = typeof claims?.exp === 'number' ? claims.exp : undefined;
  if (!exp) {
    return undefined;
  }

  const ttl = exp * 1000 - Date.now();
  return ttl > 0 ? ttl : undefined;
};

const normalizeDemoHandle = (value?: string | null) => (value ?? '').trim().toLowerCase();

const demoUserMatchesHandle = (user: DemoUser, handle: string) => {
  const handles = [user.username, user.email, ...(user.aliases ?? [])];
  return handles.some((entry) => normalizeDemoHandle(entry) === handle);
};

const findDemoUserByIdentifier = (users: DemoUser[], identifier: string): DemoUser | undefined => {
  const normalized = normalizeDemoHandle(identifier);
  if (!normalized) {
    return undefined;
  }
  return users.find((candidate) => demoUserMatchesHandle(candidate, normalized));
};

const mapDemoClaimsToExtendedUser = (
  claims: any,
  token: string,
  metadata?: { email?: string; name?: string; username?: string },
): ExtendedUser | null => {
  if (!claims || typeof claims !== 'object') {
    return null;
  }

  const tenantRolesSource =
    normalizeTenantRoleClaim(claims?.tenant_roles_map) ??
    normalizeTenantRoleClaim(claims?.tenant_roles);
  const tenantRolesMap = tenantRolesSource ? cloneTenantRoleMap(tenantRolesSource) : undefined;
  const normalizedRoles = tenantRolesMap
    ? Array.from(new Set(Object.values(tenantRolesMap).flat()))
    : normalizeRoles(claims?.roles);

  const claimedTenantId =
    typeof claims?.tenant_id === 'string' && claims.tenant_id.trim()
      ? claims.tenant_id.trim()
      : undefined;
  const currentTenantClaim =
    typeof claims?.current_tenant === 'string' && claims.current_tenant.trim()
      ? claims.current_tenant.trim()
      : undefined;
  const fallbackTenant =
    currentTenantClaim ||
    claimedTenantId ||
    (tenantRolesMap ? Object.keys(tenantRolesMap)[0] : undefined) ||
    'default';

  const tenantUuidMap = normalizeTenantUuidMap(
    claims?.tenant_uuid_map || (claims as Record<string, unknown>)?.tenantUuidMap,
  );
  const fallbackTenantUuid = resolveTenantUuidFromMap(fallbackTenant, tenantUuidMap);

  const tenantRoles =
    tenantRolesMap && Object.keys(tenantRolesMap).length > 0
      ? tenantRolesMap
      : normalizedRoles.length
        ? { [fallbackTenant]: [...normalizedRoles] }
        : undefined;

  const email =
    (typeof claims?.email === 'string' && claims.email) ||
    metadata?.email ||
    (typeof claims?.sub === 'string' ? claims.sub : '');

  const displayName =
    (typeof claims?.name === 'string' && claims.name) ||
    metadata?.name ||
    email ||
    metadata?.username ||
    'Demo User';

  const userId =
    (typeof claims?.sub === 'string' && claims.sub) ||
    email ||
    (metadata?.username ? `demo:${metadata.username}` : `demo:${Date.now()}`);

  return {
    id: userId,
    email,
    name: displayName,
    profile: {
      name: displayName,
      email,
      given_name: typeof claims?.given_name === 'string' ? claims.given_name : undefined,
      family_name: typeof claims?.family_name === 'string' ? claims.family_name : undefined,
      picture: typeof claims?.picture === 'string' ? claims.picture : undefined,
    },
    access_token: token,
    expired: false,
    tenant_roles: tenantRoles,
    current_tenant: fallbackTenant || null,
    tenant_id: fallbackTenant,
    tenant_uuid_map: tenantUuidMap,
    tenant_uuid: fallbackTenantUuid || null,
    current_tenant_uuid: fallbackTenantUuid || null,
    roles: normalizedRoles,
  };
};

const trimTrailingSlash = (value?: string | null) => (value ?? '').replace(/\/$/, '');

const resolveAuthUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const platformGateway = trimTrailingSlash(
    alignLoopbackBaseToCurrentHost(import.meta.env.VITE_PLATFORM_GATEWAY_URL),
  );
  const platformEndpoint = trimTrailingSlash(
    alignLoopbackBaseToCurrentHost(import.meta.env.VITE_PLATFORM_ENV_URL),
  );
  const apiBase = trimTrailingSlash(
    alignLoopbackBaseToCurrentHost(import.meta.env.VITE_API_BASE_URL),
  );
  const currentOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin.replace(/\/$/, '')
      : '';
  const isAuthRoute = normalizedPath.startsWith('/auth/');

  if (isAuthRoute) {
    // Backend auth endpoints are served by the API gateway. The browser-side
    // OIDC callback stays on /auth/callback, but session/logout/refresh calls
    // must go through the public API edge (for OVH: /api/auth/*).
    if (apiBase) {
      const authApiBase = apiBase.replace(/\/api\/v\d+$/i, '/api');
      return `${authApiBase}${normalizedPath}`;
    }
    if (platformEndpoint) {
      const authApiBase = platformEndpoint.match(/\/api(?:\/v\d+)?$/i)
        ? platformEndpoint.replace(/\/api\/v\d+$/i, '/api')
        : `${platformEndpoint}/api`;
      return `${authApiBase}${normalizedPath}`;
    }
    if (platformGateway) {
      return `${platformGateway}/api${normalizedPath}`;
    }
    if (currentOrigin) {
      return `${currentOrigin}/api${normalizedPath}`;
    }
    return `/api${normalizedPath}`;
  }

  const authBase = trimTrailingSlash(import.meta.env.VITE_AUTH_BASE_URL);
  if (authBase) {
    return `${authBase}${normalizedPath}`;
  }
  if (platformEndpoint) {
    return `${platformEndpoint}${normalizedPath}`;
  }
  if (apiBase) {
    const withoutApiSuffix = apiBase.replace(/\/api(\/v\d+)?$/i, '');
    return `${withoutApiSuffix}${normalizedPath}`;
  }
  return normalizedPath;
};

const resolveLogoutRedirect = (): string => {
  const resolveOrigin = (rawOrigin: string): string => {
    try {
      const url = new URL(rawOrigin);
      return url.origin.replace(/\/$/, '');
    } catch {
      return rawOrigin.replace(/\/$/, '');
    }
  };

  const redirectUri =
    typeof oidcConfig.redirect_uri === 'string' ? oidcConfig.redirect_uri.trim() : '';
  const redirectOrigin = redirectUri ? resolveOrigin(redirectUri) : '';

  const browserOrigin =
    typeof window !== 'undefined' && window.location?.origin
      ? resolveOrigin(window.location.origin)
      : '';
  const apiOrigin = (() => {
    const apiBase = trimTrailingSlash(import.meta.env.VITE_API_BASE_URL);
    if (!apiBase) return '';
    try {
      return resolveOrigin(new URL(apiBase).origin);
    } catch {
      return '';
    }
  })();
  const shouldUseCandidate = (candidate: string) => {
    try {
      const candidateOrigin = resolveOrigin(new URL(candidate).origin);
      if (
        apiOrigin &&
        candidateOrigin === apiOrigin &&
        redirectOrigin &&
        candidateOrigin !== redirectOrigin
      ) {
        return false;
      }
    } catch {
      return true;
    }
    return true;
  };

  if (browserOrigin) {
    const candidate = `${browserOrigin}/login`;
    if (shouldUseCandidate(candidate)) {
      return candidate;
    }
  }

  const explicit = trimTrailingSlash(import.meta.env.VITE_AUTH_LOGOUT_REDIRECT_URI);
  if (explicit) {
    if (explicit.startsWith('/')) {
      const base = redirectOrigin || browserOrigin;
      if (base) {
        const candidate = `${base}${explicit}`;
        if (shouldUseCandidate(candidate)) {
          return candidate;
        }
      }
    }
    try {
      const explicitOrigin = new URL(explicit).origin;
      if ((!redirectOrigin || explicitOrigin === redirectOrigin) && shouldUseCandidate(explicit)) {
        return explicit;
      }
    } catch {
      if (shouldUseCandidate(explicit)) {
        return explicit;
      }
    }
  }

  const configured =
    typeof oidcConfig.post_logout_redirect_uri === 'string'
      ? oidcConfig.post_logout_redirect_uri.trim()
      : '';
  if (configured) {
    try {
      const configuredOrigin = new URL(configured).origin;
      if (
        (!redirectOrigin || configuredOrigin === redirectOrigin) &&
        shouldUseCandidate(configured)
      ) {
        return configured;
      }
    } catch {
      if (shouldUseCandidate(configured)) {
        return configured;
      }
    }
  }

  if (redirectOrigin) {
    return `${redirectOrigin}/login`;
  }

  if (browserOrigin) {
    return `${browserOrigin}/login`;
  }
  return '/login';
};

const resolveOidcLogoutUrl = (
  redirectTarget: string | null,
  idTokenHint?: string | null,
): string | null => {
  const authority = trimTrailingSlash(oidcAuthority);
  if (!authority) {
    return null;
  }

  try {
    const logoutUrl = new URL(`${authority}/protocol/openid-connect/logout`);
    logoutUrl.searchParams.set('client_id', oidcClientId);
    if (redirectTarget) {
      logoutUrl.searchParams.set('post_logout_redirect_uri', redirectTarget);
    }
    if (idTokenHint) {
      logoutUrl.searchParams.set('id_token_hint', idTokenHint);
    }
    return logoutUrl.toString();
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[auth] Failed to construct OIDC logout URL', error);
    }
    return null;
  }
};

const normalizeRoles = (roles: unknown): string[] => canonicalizeRoles(roles);

type SessionInitResult = 'restored' | 'unauthenticated' | 'unreachable';

const mapSessionUserToExtended = (sessionUser: any): ExtendedUser => {
  const tenantId =
    (typeof sessionUser?.current_tenant === 'string' && sessionUser.current_tenant) ||
    (typeof sessionUser?.tenant_id === 'string' && sessionUser.tenant_id) ||
    undefined;

  const roles = normalizeRoles(sessionUser?.roles);
  const normalizedTenantRoles = normalizeTenantRoleClaim(sessionUser?.tenant_roles);
  let tenantRolesMap = normalizedTenantRoles
    ? cloneTenantRoleMap(normalizedTenantRoles)
    : undefined;

  if (
    (!tenantRolesMap || Object.keys(tenantRolesMap).length === 0) &&
    tenantId &&
    roles.length > 0
  ) {
    tenantRolesMap = { [tenantId]: [...roles] };
  }

  const tenantUuidMap = normalizeTenantUuidMap(
    sessionUser?.tenant_uuid_map || sessionUser?.tenantUuidMap,
  );
  const tenantUuid =
    resolveTenantUuidFromMap(tenantId, tenantUuidMap) ||
    (typeof sessionUser?.tenant_uuid === 'string' ? sessionUser.tenant_uuid : undefined);
  const currentTenantUuid =
    (typeof sessionUser?.current_tenant_uuid === 'string' && sessionUser.current_tenant_uuid) ||
    tenantUuid ||
    null;

  const displayName =
    sessionUser?.name || sessionUser?.email || sessionUser?.sub || 'Authenticated User';

  return {
    id: String(sessionUser?.sub ?? ''),
    email: sessionUser?.email || '',
    name: displayName,
    profile: {
      name: displayName,
      email: sessionUser?.email || '',
      given_name: sessionUser?.given_name,
      family_name: sessionUser?.family_name,
      picture: sessionUser?.picture,
    },
    access_token: sessionUser?.access_token || '',
    expired: false,
    tenant_roles: tenantRolesMap,
    current_tenant: tenantId,
    tenant_id: tenantId,
    tenant_uuid_map: tenantUuidMap,
    tenant_uuid: tenantUuid ?? null,
    current_tenant_uuid: currentTenantUuid,
    roles,
  };
};

const deriveSessionTtl = (sessionUser: any): number | undefined => {
  const exp = typeof sessionUser?.exp === 'number' ? sessionUser.exp : undefined;
  if (!exp) {
    return undefined;
  }

  const ttl = exp * 1000 - Date.now();
  return ttl > 0 ? ttl : undefined;
};

const deriveOidcTtl = (oidcUser: OidcUser): number | undefined => {
  const exp = typeof oidcUser?.expires_at === 'number' ? oidcUser.expires_at : undefined;
  if (!exp) {
    return undefined;
  }

  const ttl = exp * 1000 - Date.now();
  return ttl > 0 ? ttl : undefined;
};

const mapOidcUserToExtended = (oidcUser: OidcUser): ExtendedUser => {
  const profile = oidcUser.profile as OidcUserProfile;
  const tenantId = typeof profile.tenant_id === 'string' ? profile.tenant_id : undefined;

  const roleAccumulator = new Set<string>();
  const appendRoles = (source?: unknown) => {
    if (!Array.isArray(source)) return;
    source.forEach((role) => {
      if (typeof role === 'string' && role.trim().length > 0) {
        roleAccumulator.add(role);
      }
    });
  };

  appendRoles(profile.roles);
  appendRoles(profile.realm_access?.roles);
  if (profile.resource_access) {
    Object.values(profile.resource_access).forEach((entry) => appendRoles(entry?.roles));
    appendRoles(profile.resource_access?.[oidcClientId]?.roles);
  }
  const tenantRolesFromMap = normalizeTenantRoleClaim(profile.tenant_roles_map);
  const tenantRolesFromClaim = normalizeTenantRoleClaim(profile.tenant_roles);
  let tenantRolesMap = mergeTenantRoleMaps(tenantRolesFromMap, tenantRolesFromClaim);
  const tenantUuidMap = normalizeTenantUuidMap(
    profile.tenant_uuid_map || (profile as Record<string, unknown>)?.tenantUuidMap,
  );

  if (tenantRolesMap) {
    Object.values(tenantRolesMap).forEach((roles) => appendRoles(roles));
  }

  const normalizedRoles = normalizeRoles(Array.from(roleAccumulator));

  if (
    (!tenantRolesMap || Object.keys(tenantRolesMap).length === 0) &&
    tenantId &&
    normalizedRoles.length > 0
  ) {
    tenantRolesMap = { [tenantId]: [...normalizedRoles] };
  }

  const displayName =
    (typeof profile.name === 'string' && profile.name) ||
    (typeof profile.preferred_username === 'string' && profile.preferred_username) ||
    (typeof profile.email === 'string' && profile.email) ||
    profile.sub;

  const email = typeof profile.email === 'string' ? profile.email : '';
  const currentTenant = tenantId || (tenantRolesMap && Object.keys(tenantRolesMap)[0]) || undefined;
  const canonicalTenantUuid =
    resolveTenantUuidFromMap(currentTenant, tenantUuidMap) ||
    (typeof profile.tenant_uuid === 'string' ? profile.tenant_uuid : undefined);
  const currentTenantUuid =
    (typeof profile.current_tenant_uuid === 'string' && profile.current_tenant_uuid) ||
    canonicalTenantUuid ||
    null;

  return {
    id: profile.sub,
    email,
    name: displayName,
    profile: {
      name: displayName,
      email,
      given_name: typeof profile.given_name === 'string' ? profile.given_name : undefined,
      family_name: typeof profile.family_name === 'string' ? profile.family_name : undefined,
      picture: typeof profile.picture === 'string' ? profile.picture : undefined,
    },
    access_token: resolveApiBearerToken(oidcUser),
    refresh_token: oidcUser.refresh_token || undefined,
    expired: oidcUser.expired || false,
    tenant_roles: tenantRolesMap,
    current_tenant: currentTenant,
    tenant_id: tenantId,
    tenant_uuid_map: tenantUuidMap,
    tenant_uuid: canonicalTenantUuid ?? null,
    current_tenant_uuid: currentTenantUuid,
    roles: normalizedRoles,
  };
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<string | null>(null);
  const [currentTenantUuid, setCurrentTenantUuid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  const [userManager, setUserManager] = useState<UserManager | null>(null);
  const queryClient = useQueryClient();
  const setSessionScopes = useSessionStore((state) => state.setScopes);
  const userManagerRef = useRef<UserManager | null>(null);
  const oidcRedirectInFlightRef = useRef(false);
  const runtimeAuthMode = resolveAuthMode();
  const isDemoRuntime = runtimeAuthMode === 'demo';
  const isHybridRuntime = runtimeAuthMode === 'hybrid';
  const demoAuthAvailable = runtimeAuthMode !== 'oidc';
  const preferDemoAuth = isDemoRuntime && demoAuthAvailable;
  const demoFallbackEnabled = isHybridRuntime && demoAuthAvailable;
  const clearClientState = (options?: { broadcast?: boolean; rememberLogout?: boolean }) => {
    if (options?.broadcast) {
      authStore.logout();
    } else {
      authStore.clear();
    }
    authStore.clearAllStorage();
    setUser(null);
    setIsAuthenticated(false);
    applyTenantSelection(null);
    setSessionScopes([]);
    queryClient.clear();
    setIsLoading(false);
    setAuthResolved(true);
    if (options?.rememberLogout) {
      markLoggedOut();
    }
  };
  const applyTenantSelection = (slug: string | null, explicitUuid?: string | null) => {
    setCurrentTenant(slug);
    if (!slug) {
      setCurrentTenantUuid(null);
      return;
    }
    const resolved = explicitUuid ?? resolveTenantUuidFromMap(slug, user?.tenant_uuid_map);
    setCurrentTenantUuid(resolved);
  };

  const ensureUserManager = (): UserManager | null => {
    if (userManagerRef.current) {
      return userManagerRef.current;
    }
    try {
      const createdManager = new UserManager(oidcConfig);
      userManagerRef.current = createdManager;
      setUserManager(createdManager);
      return createdManager;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Failed to initialize OIDC user manager', error);
      }
      return null;
    }
  };

  const login = useCallback(
    async (returnPath?: string, options?: { loginHint?: string }) => {
      setIsLoading(true);
      setAuthResolved(false);
      clearLogoutGuard();

      if (returnPath) {
        sessionStorage.setItem('rugira_return_path', returnPath);
      }

      if (preferDemoAuth) {
        window.location.href =
          '/login' + (returnPath ? `?next=${encodeURIComponent(returnPath)}` : '');
        return;
      }

      const manager = ensureUserManager();
      if (manager) {
        try {
          if (oidcRedirectInFlightRef.current || hasRecentOidcLoginAttempt()) {
            if (import.meta.env.DEV) {
              console.log(
                '[auth] OIDC redirect already in-flight; skipping duplicate signinRedirect',
              );
            }
            return;
          }
          oidcRedirectInFlightRef.current = true;
          const redirectParams: Record<string, unknown> = {
            state: returnPath || '/overview',
          };
          if (options?.loginHint) {
            redirectParams.extraQueryParams = { login_hint: options.loginHint };
          }
          markOidcLoginAttempt();
          await manager.signinRedirect(redirectParams);
        } catch (error) {
          console.error('Login failed:', error);
          oidcRedirectInFlightRef.current = false;
          clearOidcLoginAttempt();
          setIsLoading(false);
          setAuthResolved(true);
          throw new Error(
            'Unable to reach Rugira sign-in. Check that the Rugira identity provider is running, then retry.',
            { cause: error },
          );
        }
      } else {
        oidcRedirectInFlightRef.current = false;
        clearOidcLoginAttempt();
        setIsLoading(false);
        setAuthResolved(true);
        throw new Error(
          'Unable to initialize Rugira sign-in. Check the local OIDC configuration, then retry.',
        );
      }
    },
    [preferDemoAuth],
  );

  useEffect(() => {
    userManagerRef.current = userManager;
  }, [userManager]);

  const purgeOidcState = () => {
    const manager = userManagerRef.current;
    if (manager) {
      manager.removeUser().catch((error) => {
        if (import.meta.env.DEV) {
          console.warn('Failed to clear OIDC user cache', error);
        }
      });
    }
  };

  const setupAuthEventListeners = () => {
    const handleLocked = () => {
      // Skip if logout is already in progress to prevent redirect loops
      if (isLogoutGuardActive()) {
        if (import.meta.env.DEV) {
          console.log('[auth] auth:locked ignored - logout guard active');
        }
        return;
      }
      setOidcAutoLoginBlock('session_locked');
      purgeOidcState();
      clearClientState();
      window.location.href = '/';
    };

    const handleExpired = () => {
      // Skip if logout is already in progress to prevent redirect loops
      if (isLogoutGuardActive()) {
        if (import.meta.env.DEV) {
          console.log('[auth] auth:expired ignored - logout guard active');
        }
        return;
      }
      setOidcAutoLoginBlock('session_expired');
      purgeOidcState();
      clearClientState();
      window.location.href = '/';
    };

    window.addEventListener('auth:locked', handleLocked);
    window.addEventListener('auth:expired', handleExpired);

    // Listen for OIDC silent-renew failures (e.g. Keycloak session expired)
    // so the user is prompted to re-login instead of silently losing auth.
    const manager = userManagerRef.current;
    const handleSilentRenewError = (error: Error) => {
      if (import.meta.env.DEV) {
        console.warn('[auth] Silent renew failed, triggering session expiry', error);
      }
      window.dispatchEvent(
        new CustomEvent('auth:expired', {
          detail: { reason: 'silent_renew_failed' },
        }),
      );
    };

    const handleAccessTokenExpired = () => {
      if (import.meta.env.DEV) {
        console.warn('[auth] Access token expired');
      }
      window.dispatchEvent(
        new CustomEvent('auth:expired', {
          detail: { reason: 'access_token_expired' },
        }),
      );
    };

    if (manager) {
      manager.events.addSilentRenewError(handleSilentRenewError);
      manager.events.addAccessTokenExpired(handleAccessTokenExpired);
    }

    return () => {
      window.removeEventListener('auth:locked', handleLocked);
      window.removeEventListener('auth:expired', handleExpired);
      if (manager) {
        manager.events.removeSilentRenewError(handleSilentRenewError);
        manager.events.removeAccessTokenExpired(handleAccessTokenExpired);
      }
    };
  };

  const initDemoAuth = async (isCancelled: () => boolean) => {
    const logoutGuarded = isLogoutGuardActive();
    if (logoutGuarded) {
      authStore.logout();
      setUser(null);
      setIsAuthenticated(false);
      applyTenantSelection(null);
      return;
    }

    const storedUser = authStore.getUser() as ExtendedUser | null;
    const hasValidSession = Boolean(storedUser) && !authStore.isExpired();
    if (!isCancelled() && storedUser && hasValidSession) {
      setUser(storedUser);
      setIsAuthenticated(true);
      applyTenantSelection(
        storedUser.current_tenant || storedUser.tenant_id || 'default',
        storedUser.current_tenant_uuid || storedUser.tenant_uuid || null,
      );
      return;
    }

    authStore.clear();
    setUser(null);
    setIsAuthenticated(false);
    applyTenantSelection(null);

    if (isCancelled()) {
      return;
    }

    try {
      const { demoUsers } = await import('@/lib/demoUsers');
      if (!demoUsers.length) {
        return;
      }

      const DEFAULT_DEMO_ADMIN = 'platform.admin';
      const preferredHandle = (import.meta.env.VITE_DEMO_AUTO_USER || DEFAULT_DEMO_ADMIN).trim();
      const fallbackUser =
        findDemoUserByIdentifier(demoUsers, preferredHandle) ??
        findDemoUserByIdentifier(demoUsers, DEFAULT_DEMO_ADMIN) ??
        demoUsers[0];

      if (!fallbackUser) {
        return;
      }

      await performDemoLogin(fallbackUser.username, fallbackUser.password, '/overview');
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Failed to initialise demo auth session', error);
      }
    }
  };

  const initSessionAuth = async (isCancelled: () => boolean): Promise<SessionInitResult> => {
    try {
      const response = await fetch(resolveAuthUrl('/auth/session'), {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      });

      if (isCancelled()) {
        return 'unauthenticated';
      }

      if (!response.ok) {
        if (response.status === 401) {
          authStore.logout();
          setUser(null);
          setIsAuthenticated(false);
          applyTenantSelection(null);
          return 'unauthenticated';
        }
        if (import.meta.env.DEV) {
          console.warn('Session check failed with status', response.status);
        }
        setUser(null);
        setIsAuthenticated(false);
        applyTenantSelection(null);
        authStore.clear();
        return 'unreachable';
      }

      const payload = (await response.json()) as { authenticated?: boolean; user?: any };
      if (isCancelled()) {
        return 'unauthenticated';
      }

      if (payload?.authenticated && payload.user) {
        const mappedUser = mapSessionUserToExtended(payload.user);
        const ttlMs = deriveSessionTtl(payload.user);
        setUser(mappedUser);
        setIsAuthenticated(true);
        applyTenantSelection(
          mappedUser.current_tenant || mappedUser.tenant_id || null,
          mappedUser.current_tenant_uuid || mappedUser.tenant_uuid || null,
        );
        persistSessionUser(mappedUser, ttlMs);
        return 'restored';
      }

      setUser(null);
      setIsAuthenticated(false);
      applyTenantSelection(null);
      authStore.clear();
      return 'unauthenticated';
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Failed to initialise authenticated session', error);
      }
      setUser(null);
      setIsAuthenticated(false);
      applyTenantSelection(null);
      authStore.clear();
      return 'unreachable';
    }
  };

  useEffect(() => {
    let cancelled = false;
    const checkCancelled = () => cancelled;
    const teardown = setupAuthEventListeners();

    if (import.meta.env.DEV) {
      console.log('[auth] Bootstrap effect running, path:', window.location.pathname);
    }

    const bootstrap = async () => {
      if (import.meta.env.DEV) {
        console.log('[auth] Bootstrap starting...');
      }
      setIsLoading(true);
      setAuthResolved(false);

      const wasLoggedOut = isLogoutGuardActive();
      if (wasLoggedOut) {
        if (import.meta.env.DEV) {
          console.log('[auth] Logout guard detected - preventing session restoration');
        }
        // Keep this path synchronous and non-destructive. Clearing all storage or
        // hitting /auth/logout here can race with a user-initiated retry login and
        // wipe OIDC transient callback state, causing callback/login loops.
        clearClientState();
        return;
      }

      if (preferDemoAuth) {
        await initDemoAuth(checkCancelled);
      } else {
        const manager = ensureUserManager();

        const sessionOutcome = await initSessionAuth(checkCancelled);
        const sessionRestored = sessionOutcome === 'restored';
        const sessionUnreachable = sessionOutcome === 'unreachable';
        let restoredFromOidc = false;

        if (!sessionRestored && allowBearerFallback && manager) {
          try {
            const existingUser = await manager.getUser();
            if (!checkCancelled() && existingUser && !existingUser.expired) {
              const mapped = mapOidcUserToExtended(existingUser);
              const ttlMs = deriveOidcTtl(existingUser);
              setUser(mapped);
              setIsAuthenticated(true);
              applyTenantSelection(
                mapped.current_tenant || mapped.tenant_id || null,
                mapped.current_tenant_uuid || mapped.tenant_uuid || null,
              );
              persistAuth(
                mapped.access_token,
                existingUser.refresh_token || undefined,
                mapped,
                ttlMs,
              );
              restoredFromOidc = true;
            }
          } catch (error) {
            if (import.meta.env.DEV) {
              console.warn('Failed to restore OIDC session', error);
            }
          }
        }

        if (!sessionRestored && !restoredFromOidc) {
          // Skip auto-redirect to Keycloak if we're on the auth callback page.
          // The callback page handles its own OIDC flow and should not be interrupted.
          const isOnCallbackPage =
            typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/callback');

          if (
            sessionUnreachable &&
            !demoFallbackEnabled &&
            !preferDemoAuth &&
            !isOnCallbackPage &&
            !checkCancelled()
          ) {
            if (import.meta.env.DEV) {
              console.warn('[auth] Session endpoint unreachable; pausing automatic OIDC redirect.');
            }
            setOidcAutoLoginBlock('session_unreachable');
            authStore.clear();
            setUser(null);
            setIsAuthenticated(false);
            applyTenantSelection(null);
          }

          if (demoFallbackEnabled && !checkCancelled()) {
            if (import.meta.env.DEV) {
              console.warn(
                '[auth] Falling back to demo mode because OIDC session bootstrap failed.',
              );
            }
            await initDemoAuth(checkCancelled);
          } else if (!allowBearerFallback && !checkCancelled()) {
            authStore.logout();
            setUser(null);
            setIsAuthenticated(false);
            applyTenantSelection(null);
          }
        }
      }

      if (!checkCancelled()) {
        setAuthResolved(true);
        setIsLoading(false);
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      teardown?.();
    };
  }, [preferDemoAuth, demoFallbackEnabled, login]);
  async function performDemoLogin(
    username: string,
    password: string,
    returnPath?: string,
  ): Promise<'demo' | 'oidc' | false> {
    if (!demoAuthAvailable) return false;

    const identifier = (username ?? '').trim();
    const secret = (password ?? '').trim();
    const targetPath = returnPath || '/overview';

    if (!identifier || !secret) {
      setIsLoading(false);
      setAuthResolved(true);
      return false;
    }

    if (!preferDemoAuth) {
      try {
        await login(targetPath, { loginHint: identifier });
        return 'oidc';
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('Demo login redirect failed', error);
        }
        setIsLoading(false);
        setAuthResolved(true);
        return false;
      }
    }

    try {
      const { demoUsers } = await import('@/lib/demoUsers');
      const matchedUser = findDemoUserByIdentifier(demoUsers, identifier);
      const email = matchedUser?.email ?? identifier;
      const demoTenant = import.meta.env.VITE_DEV_TENANT_ID || 'default';
      const loginBody = {
        username: email,
        email,
        password: secret,
        tenant: demoTenant,
        tenantId: demoTenant,
      };

      const invokeAuthEndpoint = async (path: string) => {
        const response = await fetch(resolveAuthUrl(path), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'include',
          body: JSON.stringify(loginBody),
        });
        let payload: Record<string, any> | null;
        try {
          payload = (await response.json()) as Record<string, any>;
        } catch {
          payload = null;
        }
        return { response, payload };
      };

      let { response, payload } = await invokeAuthEndpoint('/auth/login');
      if (response.status === 404) {
        if (import.meta.env.DEV) {
          console.warn('[auth] /auth/login not available; attempting legacy /auth/login_demo.');
        }
        ({ response, payload } = await invokeAuthEndpoint('/auth/login_demo'));
      }

      if (!response.ok || payload?.ok === false) {
        const detail =
          (payload?.message as string | undefined) ||
          (payload?.detail as string | undefined) ||
          'Invalid username or password. Please try again.';
        throw new Error(detail);
      }

      if (payload?.access_token) {
        const claims = decodeJwtClaims(payload.access_token);
        if (!claims) {
          throw new Error('Unable to parse demo token payload.');
        }

        const mappedUser = mapDemoClaimsToExtendedUser(claims, payload.access_token, {
          email,
          name: matchedUser?.name,
          username: identifier,
        });
        if (!mappedUser) {
          throw new Error('Demo token did not contain role assignments.');
        }

        const ttlMs = deriveJwtTtlFromClaims(claims);
        setUser(mappedUser);
        setIsAuthenticated(true);
        applyTenantSelection(
          mappedUser.current_tenant || mappedUser.tenant_id || null,
          mappedUser.current_tenant_uuid || mappedUser.tenant_uuid || null,
        );
        persistAuth(mappedUser.access_token, undefined, mappedUser, ttlMs);
        setAuthResolved(true);
        setIsLoading(false);
        return 'demo';
      }

      const sessionResult = await initSessionAuth(() => false);
      if (sessionResult !== 'restored') {
        throw new Error('Unable to establish demo session. Please retry.');
      }
      setAuthResolved(true);
      setIsLoading(false);
      return 'demo';
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('Demo login failed', error);
      }
      setIsLoading(false);
      setAuthResolved(true);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Demo login failed. Please try again.', { cause: error });
    }
  }

  const logout = async () => {
    const manager = ensureUserManager();
    let oidcUser: OidcUser | null = null;
    if (manager) {
      try {
        oidcUser = await manager.getUser();
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[auth] Failed to read OIDC user before logout', error);
        }
      }
    }

    // Clear return path and prepare redirect target first
    sessionStorage.removeItem('rugira_return_path');
    const logoutRedirectTarget = resolveLogoutRedirect();
    const oidcLogoutUrl = resolveOidcLogoutUrl(logoutRedirectTarget, oidcUser?.id_token);
    const oidcLogoutUrlNoRedirect = resolveOidcLogoutUrl(null, oidcUser?.id_token);
    clearOidcLoginAttempt();

    if (import.meta.env.DEV) {
      console.log('[auth] Starting logout flow, redirect target:', logoutRedirectTarget);
    }

    const redirectToLogin = () => {
      if (typeof window !== 'undefined') {
        if (import.meta.env.DEV) {
          console.log('[auth] Redirecting to login page:', logoutRedirectTarget);
        }
        window.location.replace(logoutRedirectTarget);
      }
    };

    const fireAndForgetOidcLogout = (url: string) => {
      try {
        const img = new Image();
        img.referrerPolicy = 'no-referrer';
        img.src = url;
      } catch {
        /* ignore */
      }
      try {
        fetch(url, {
          method: 'GET',
          mode: 'no-cors',
          credentials: 'include',
          keepalive: true,
        }).catch(() => undefined);
      } catch {
        /* ignore */
      }
    };

    // Mark logout guard FIRST to prevent session restoration on page load
    markLoggedOut();

    // Call backend logout BEFORE clearing client state to ensure cookie is invalidated
    // Also send refresh token for server-side revocation at Keycloak
    let backendLogoutSucceeded: boolean;
    try {
      const response = await fetch(resolveAuthUrl('/auth/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refreshToken: oidcUser?.refresh_token || user?.refresh_token,
        }),
      });
      backendLogoutSucceeded = response.ok;
      if (import.meta.env.DEV) {
        const data = await response.json().catch(() => ({}));
        console.log(
          '[auth] Backend logout response:',
          response.status,
          backendLogoutSucceeded ? 'OK' : 'FAILED',
          data,
        );
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[auth] Logout endpoint request failed', error);
      }
    }

    // Clear OIDC user from UserManager storage BEFORE signoutRedirect
    // This is critical - even if signoutRedirect fails, the local session is cleared
    if (manager) {
      try {
        await manager.removeUser();
        if (import.meta.env.DEV) {
          console.log('[auth] OIDC user removed from UserManager storage');
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[auth] Failed to remove OIDC user from storage', error);
        }
      }
    }

    // Clear client state AFTER backend logout attempt
    clearClientState({ broadcast: true, rememberLogout: true });

    // Ensure the logout guard is DEFINITELY set before any redirects
    // This is critical - AuthCallback checks for this guard to prevent auto-login
    markLoggedOut();
    if (import.meta.env.DEV) {
      console.log('[auth] Logout guard set, value:', sessionStorage.getItem('rugira_logout_guard'));
    }

    if (preferDemoAuth) {
      redirectToLogin();
      return;
    }

    // Use fire-and-forget approach for OIDC logout to avoid Keycloak redirect URI issues
    // This ends the Keycloak session in the background while we redirect to login directly
    if (oidcLogoutUrlNoRedirect) {
      if (import.meta.env.DEV) {
        console.log('[auth] Triggering background OIDC logout (no redirect)');
      }
      fireAndForgetOidcLogout(oidcLogoutUrlNoRedirect);
    } else if (oidcLogoutUrl) {
      if (import.meta.env.DEV) {
        console.log('[auth] Triggering background OIDC logout with redirect target');
      }
      fireAndForgetOidcLogout(oidcLogoutUrl);
    }

    // Always redirect to login page directly
    redirectToLogin();
  };

  const getAccessToken = () => {
    return user?.access_token || null;
  };

  const handleRedirectCallback = async (): Promise<string> => {
    if (preferDemoAuth) {
      const returnPath = sessionStorage.getItem('rugira_return_path') || '/overview';
      sessionStorage.removeItem('rugira_return_path');
      await initDemoAuth(() => false);
      setAuthResolved(true);
      setIsLoading(false);
      return returnPath;
    }

    const manager = ensureUserManager();
    if (manager) {
      try {
        if (import.meta.env.DEV) {
          console.log('[auth] handleRedirectCallback: calling signinRedirectCallback...');
        }
        const oidcUser = await manager.signinRedirectCallback();
        if (import.meta.env.DEV) {
          console.log('[auth] handleRedirectCallback: success, user:', oidcUser?.profile?.email);
        }
        const mapped = mapOidcUserToExtended(oidcUser);
        const ttlMs = deriveOidcTtl(oidcUser);
        setUser(mapped);
        setIsAuthenticated(true);
        applyTenantSelection(
          mapped.current_tenant || mapped.tenant_id || null,
          mapped.current_tenant_uuid || mapped.tenant_uuid || null,
        );
        persistAuth(mapped.access_token, oidcUser.refresh_token || undefined, mapped, ttlMs);
        setAuthResolved(true);
        setIsLoading(false);
        const returnPath = typeof oidcUser.state === 'string' ? oidcUser.state : '/overview';
        oidcRedirectInFlightRef.current = false;
        return returnPath;
      } catch (error: unknown) {
        oidcRedirectInFlightRef.current = false;
        clearOidcLoginAttempt();
        // Check if this is a "code already used" error from React Strict Mode double-execution
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isCodeAlreadyUsed =
          errorMessage.includes('invalid_grant') ||
          errorMessage.includes('Code not valid') ||
          errorMessage.includes('code') ||
          errorMessage.includes('already');

        if (import.meta.env.DEV) {
          console.error('[auth] handleRedirectCallback failed:', errorMessage);
          if (isCodeAlreadyUsed) {
            console.log(
              '[auth] This may be due to React Strict Mode double-execution, attempting recovery...',
            );
          }
        }

        // If the code was already used (Strict Mode), try to get the existing user from store
        if (isCodeAlreadyUsed) {
          try {
            const existingUser = await manager.getUser();
            if (existingUser && !existingUser.expired) {
              if (import.meta.env.DEV) {
                console.log('[auth] handleRedirectCallback: recovered existing user from store');
              }
              const mapped = mapOidcUserToExtended(existingUser);
              const ttlMs = deriveOidcTtl(existingUser);
              setUser(mapped);
              setIsAuthenticated(true);
              applyTenantSelection(
                mapped.current_tenant || mapped.tenant_id || null,
                mapped.current_tenant_uuid || mapped.tenant_uuid || null,
              );
              persistAuth(
                mapped.access_token,
                existingUser.refresh_token || undefined,
                mapped,
                ttlMs,
              );
              setAuthResolved(true);
              setIsLoading(false);
              const returnPath =
                typeof existingUser.state === 'string' ? existingUser.state : '/overview';
              return returnPath;
            }
          } catch (recoveryError) {
            if (import.meta.env.DEV) {
              console.warn('[auth] Recovery from existing user failed:', recoveryError);
            }
            // Fall through to unauthenticated state
          }
        }

        authStore.clear();
        setUser(null);
        setIsAuthenticated(false);
        applyTenantSelection(null);
        setAuthResolved(true);
        setIsLoading(false);
        throw error instanceof Error ? error : new Error('OIDC callback failed');
      }
    }

    setAuthResolved(true);
    setIsLoading(false);
    return '/overview';
  };

  const switchTenant = (tenantId: string) => {
    if (user && user.tenant_roles && user.tenant_roles[tenantId]) {
      const tenantUuid =
        resolveTenantUuidFromMap(tenantId, user.tenant_uuid_map) || user.tenant_uuid || null;
      const updatedUser = {
        ...user,
        current_tenant: tenantId,
        tenant_id: tenantId,
        current_tenant_uuid: tenantUuid,
        tenant_uuid: tenantUuid ?? user.tenant_uuid,
      };
      setUser(updatedUser);
      applyTenantSelection(tenantId, tenantUuid);
      if (user.access_token) {
        persistAuth(user.access_token, undefined, updatedUser);
      } else {
        persistSessionUser(updatedUser);
      }
    }
  };

  const tenantRolesRaw = user?.tenant_roles?.[currentTenant || ''] || user?.roles || [];
  const tenantRoles = canonicalizeRoles(tenantRolesRaw);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    authResolved,
    user,
    isDemoMode: isDemoRuntime,
    currentTenant,
    currentTenantUuid,
    tenantRoles,
    login,
    logout,
    getAccessToken,
    handleRedirectCallback,
    switchTenant,
    performDemoLogin: preferDemoAuth ? performDemoLogin : undefined,
  };

  useEffect(() => {
    const claims = user?.access_token ? decodeJwtClaims(user.access_token) : null;
    const scopeClaim =
      (claims?.scope as string | undefined) ||
      (claims?.scopes as string | undefined) ||
      (user as any)?.scope ||
      null;
    setSessionScopes(scopeClaim);
  }, [user, setSessionScopes]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
export type { ExtendedUser } from '@/lib/oidcConfig';
